import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Settings } from 'lucide-react'
import { useUserRole } from '@/hooks/useUserRole'
import { useToast } from '@/hooks/use-toast'
import { useTranslation } from 'react-i18next'
import { GeneralTab } from '@/components/organization-settings/GeneralTab'
import { NotificationsTab } from '@/components/organization-settings/NotificationsTab'
import { RolesPermissionsTab } from '@/components/organization-settings/RolesPermissionsTab'
import { RetentionArchiveTab } from '@/components/organization-settings/RetentionArchiveTab'
import { BillingTab } from '@/components/organization-settings/BillingTab'
import { ExportTab } from '@/components/organization-settings/ExportTab'
import { SecurityTab } from '@/components/organization-settings/SecurityTab'
import { LogsTab } from '@/components/organization-settings/LogsTab'
import { supabase } from '@/integrations/supabase/client.safe'
import { formatWithUTC } from '@/lib/datetime'

interface Organization {
  id: string;
  name: string;
  company_name?: string;
  org_number?: string;
}

interface OrganizationSettingsProps {
  organization: Organization | null;
}

export function OrganizationSettings({ organization }: OrganizationSettingsProps) {
  const { role, loading } = useUserRole()
  const [orgState, setOrgState] = useState<Organization | null>(organization)
  const [activeTab, setActiveTab] = useState('general')
  const [lastModified, setLastModified] = useState<{ by: string; at: string } | null>(null)
  const { toast } = useToast()
  const { t } = useTranslation('common')

  // Sync local org state when prop changes
  useEffect(() => {
    setOrgState(organization)
  }, [organization])

  // Tabs available to different roles
  const getAvailableTabs = () => {
    const allTabs = [
      { value: 'general', label: t('settings.tabs.general'), adminOnly: false },
      { value: 'notifications', label: t('settings.tabs.notifications'), adminOnly: false },
      { value: 'roles', label: t('settings.tabs.roles'), adminOnly: true },
      { value: 'retention', label: t('settings.tabs.retention'), adminOnly: true },
      { value: 'billing', label: t('settings.tabs.billing'), adminOnly: true },
      { value: 'export', label: t('settings.tabs.export'), adminOnly: false },
      { value: 'security', label: t('settings.tabs.security'), adminOnly: true },
      { value: 'logs', label: t('settings.tabs.logs'), adminOnly: false }
    ]
    
    if (role === 'admin') {
      return allTabs
    } else {
      return allTabs.filter(tab => !tab.adminOnly)
    }
  }

  const availableTabs = getAvailableTabs()

  const handleSave = async (section: string, data: any) => {
    try {
      // Spara sektioner till Supabase (minimalt: Allmänna uppgifter)
      if (section === 'general') {
        const updatePayload: Record<string, any> = {}
        if (typeof data.company_name === 'string') updatePayload.company_name = data.company_name
        if (typeof data.org_number === 'string') updatePayload.org_number = data.org_number
        
        if (Object.keys(updatePayload).length > 0) {
          // Försök uppdatera befintlig organisation om möjligt, annars skapa ny
          let targetOrgId: string | null = (orgState && orgState.id !== 'default-org') ? orgState.id : null

          if (!targetOrgId) {
            // 0) Försök med localStorage först
            const stored = localStorage.getItem('active-org-id')
            if (stored) {
              targetOrgId = stored
            }
          }

          if (!targetOrgId) {
            // 1) Roll-baserad åtkomst (RPC)
            const { data: access } = await supabase.rpc('get_user_organization_access')
            if (access && access.length > 0) {
              targetOrgId = access[0].organization_id
            }
          }

          if (!targetOrgId) {
            // 2) Direkt från user_roles (fallback om RPC saknar data)
            const { data: authUser } = await supabase.auth.getUser()
            if (authUser.user) {
              const { data: roles } = await supabase
                .from('user_roles')
                .select('organization_id')
                .eq('user_id', authUser.user.id)
                .limit(1)
              if (roles && roles.length > 0) {
                targetOrgId = roles[0].organization_id as string
              }
            }
          }

          if (!targetOrgId) {
            // 3) Fallback: organisation skapad av användaren
            const { data: authUser } = await supabase.auth.getUser()
            if (authUser.user) {
              const { data: created } = await supabase
                .from('organizations')
                .select('id')
                .eq('created_by_user_id', authUser.user.id)
                .maybeSingle()
              if (created) targetOrgId = created.id as string
            }
          }

          if (targetOrgId) {
            // Säkerställ admin-roll för att kunna uppdatera (RLS kräver admin)
            const { data: auth } = await supabase.auth.getUser()
            if (!auth.user) throw new Error('User not authenticated')

            const { data: existingRole } = await supabase
              .from('user_roles')
              .select('id, role')
              .eq('user_id', auth.user.id)
              .eq('organization_id', targetOrgId)
              .maybeSingle()

            if (!existingRole || existingRole.role !== 'admin') {
              const { error: roleError } = await supabase
                .from('user_roles')
                .insert({ user_id: auth.user.id, organization_id: targetOrgId, role: 'admin' })
              // Ignorera unika konflikter, kasta övriga fel
              if (roleError && !(roleError as any).code?.startsWith?.('23')) {
                throw roleError
              }
            }

            // Uppdatera befintlig organisation och hämta tillbaka uppdaterade fält
            const { data: updated, error: updateError } = await supabase
              .from('organizations')
              .update(updatePayload)
              .eq('id', targetOrgId)
              .select()
              .maybeSingle()
            if (updateError) throw updateError

            // Om inga rader returneras (t.ex. p.g.a. RLS), försök hämta aktuell rad
            let effective = updated as any
            if (!effective) {
              const { data: refetched, error: refetchError } = await supabase
                .from('organizations')
                .select('*')
                .eq('id', targetOrgId)
                .maybeSingle()
              if (refetchError) throw refetchError
              effective = refetched
            }

            if (!effective) {
              throw new Error('Uppdateringen nekades av rättighetspolicyn.')
            }

            setOrgState({
              id: effective.id,
              name: effective.company_name || effective.name,
              company_name: effective.company_name ?? undefined,
              org_number: effective.org_number ?? undefined,
            })
            // Spara aktiv organisation i localStorage
            localStorage.setItem('active-org-id', effective.id)
          } else {
            // Skapa ny organisation (om användaren saknar en)
            const { data: auth } = await supabase.auth.getUser()
            if (!auth.user) throw new Error('User not authenticated')

            const { data: newOrg, error: createError } = await supabase
              .from('organizations')
              .insert({
                name: data.company_name || 'Din Organisation',
                company_name: data.company_name || null,
                org_number: data.org_number || null,
                created_by_user_id: auth.user.id
              })
              .select()
              .single()

            if (createError) {
              const code = (createError as any).code || ''
              const message: string = (createError as any).message || ''

              // Hantera unikt org-nummer: försök ta över outdelade organisationer
              if (code === '23505' || message.includes('organizations_org_number_uniq')) {
                // Försök reclaima organisation om den saknar roller/ägare
                const { data: claimedId, error: claimError } = await supabase
                  .rpc('claim_organization_by_org_number', { p_org_number: data.org_number })

                if (!claimError && claimedId) {
                  // Org reclaimad, hämta uppdaterad data och sätt som aktiv
                  const { data: claimedOrg, error: fetchError } = await supabase
                    .from('organizations')
                    .select('*')
                    .eq('id', claimedId)
                    .maybeSingle()

                  if (fetchError) throw fetchError

                  // Uppdatera med nya värden
                  const { data: updatedClaimed, error: updateErr } = await supabase
                    .from('organizations')
                    .update(updatePayload)
                    .eq('id', claimedId)
                    .select()
                    .maybeSingle()

                  if (updateErr) throw updateErr

                  setOrgState({
                    id: updatedClaimed.id,
                    name: updatedClaimed.company_name || updatedClaimed.name,
                    company_name: updatedClaimed.company_name ?? undefined,
                    org_number: updatedClaimed.org_number ?? undefined,
                  })
                  localStorage.setItem('active-org-id', updatedClaimed.id)
                } else {
                  throw new Error('Organisationsnumret används redan av en annan organisation.')
                }
              } else {
                throw createError
              }
            } else if (newOrg) {
              // Ge skaparen admin-roll för organisationen (krävs för framtida uppdateringar)
              const { error: roleInsertError } = await supabase
                .from('user_roles')
                .insert({ user_id: auth.user.id, organization_id: newOrg.id, role: 'admin' })
              if (roleInsertError && !(roleInsertError as any).code?.startsWith?.('23')) {
                throw roleInsertError
              }

              setOrgState({
                id: newOrg.id,
                name: newOrg.company_name || newOrg.name,
                company_name: newOrg.company_name ?? undefined,
                org_number: newOrg.org_number ?? undefined,
              })
              localStorage.setItem('active-org-id', newOrg.id)
            }
          }
        }
        // NOTE: timezone och logotyp sparas i senare steg (kräver separat tabell)
      }

      // Hämta användarnamn för "Ändrat av"
      const { data: authData } = await supabase.auth.getUser()
      let byName = 'Okänd användare'
      if (authData.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name, email')
          .eq('id', authData.user.id)
          .maybeSingle()
        byName = profile?.full_name || profile?.email || authData.user.email || byName
      }

      setLastModified({
        by: byName,
        at: formatWithUTC(new Date())
      })

      const sectionName = section === 'general' ? t('settings.tabs.general') : 
                       section === 'notifications' ? t('settings.tabs.notifications') :
                       section === 'roles' ? t('settings.tabs.roles') :
                       section === 'retention' ? t('settings.tabs.retention') :
                       section === 'billing' ? t('settings.tabs.billing') :
                       section === 'security' ? t('settings.tabs.security') :
                       section === 'export' ? t('settings.tabs.export') : section

      toast({
        title: t('settings.save.success.title'),
        description: `${sectionName} ${t('settings.save.success.description')}.`,
      })
    } catch (error: any) {
      toast({
        title: t('settings.save.error.title'),
        description: error?.message || t('settings.save.error.description'),
        variant: 'destructive'
      })
    }
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Settings className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">{t('settings.title')}</h1>
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!orgState) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Settings className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Organisationsinställningar</h1>
            <p className="text-muted-foreground">Ingen organisation hittades.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Settings className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">{t('settings.title')}</h1>
          <p className="text-muted-foreground">
            {t('settings.subtitle')} {orgState.company_name || orgState.name}
          </p>
        </div>
      </div>

      {/* Settings Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${availableTabs.length}, 1fr)` }}>
          {availableTabs.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value} className="text-sm">
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="general">
          <GeneralTab organization={orgState} onSave={(data) => handleSave('general', data)} />
        </TabsContent>

        <TabsContent value="notifications">
          <NotificationsTab organization={orgState} onSave={(data) => handleSave('notifications', data)} />
        </TabsContent>

        {role === 'admin' && (
          <>
            <TabsContent value="roles">
              <RolesPermissionsTab organization={orgState} onSave={(data) => handleSave('roles', data)} />
            </TabsContent>

            <TabsContent value="retention">
              <RetentionArchiveTab organization={orgState} onSave={(data) => handleSave('retention', data)} />
            </TabsContent>

            <TabsContent value="billing">
              <BillingTab organization={orgState} onSave={(data) => handleSave('billing', data)} />
            </TabsContent>

            <TabsContent value="security">
              <SecurityTab organization={orgState} onSave={(data) => handleSave('security', data)} />
            </TabsContent>
          </>
        )}

        <TabsContent value="export">
          <ExportTab organization={orgState} onSave={(data) => handleSave('export', data)} />
        </TabsContent>

        <TabsContent value="logs">
          <LogsTab organization={orgState} />
        </TabsContent>
      </Tabs>

      {/* Footer with last modified info */}
      {lastModified && (
        <div className="text-sm text-muted-foreground text-center border-t pt-4">
          {t('settings.lastModified')} {lastModified.by} • {lastModified.at}
        </div>
      )}
    </div>
  )
}